import { Database } from "../../supabase.types";
import { SupabaseClient } from "@supabase/supabase-js";
import { logException } from "@punchup/sentry";
import { getCDNImageURL } from "@punchup/utils/getCDNImageURL";
import { ShowWithComedian, getShowsByIds } from "../../legacy";
import { applyPublicShowPipeline } from "../public/show-pipeline";
import type { VenuePageTimezones } from "../../models/venue_pages";
import {
  VenuePageCarouselMode,
  VenueCarouselItem,
  VenueCarouselResponse,
  CarouselItemInput,
  UploadCarouselImageResponse,
  UploadCarouselVideoResponse,
  AddCarouselShowsResponse,
} from "../../models/venue-pages-carousel";

/**
 * Map adapter over applyPublicShowPipeline for the batch-fetched custom
 * carousel shows: shows past the venue-local grace window dropped, live
 * Tixologi sold-out status merged, password-protected/hidden events removed.
 * `venueTimezones` makes the expiry boundary venue-local (see
 * applyPublicShowPipeline / publicShowStartCutoff).
 */
async function filterAndEnrichShowsForPublicDisplay(
  showsMap: Map<string, ShowWithComedian>,
  venueTimezones: VenuePageTimezones
): Promise<Map<string, ShowWithComedian>> {
  const publicShows = await applyPublicShowPipeline(
    [...showsMap.values()],
    venueTimezones
  );
  return new Map(publicShows.map((show) => [show.id, show]));
}

/**
 * Fetch carousel mode and items for a venue page
 *
 * `forPublicDisplay` applies the public venue page's show pipeline to the
 * custom carousel's show items (expiry, live sold-out status, password
 * protection — see applyPublicShowPipeline). The manager dashboard omits it
 * so venue managers still see and can remove expired pinned items, and
 * manager requests make no external Tixologi calls.
 */
export async function getVenuePageCarousel(
  supabase: SupabaseClient<Database>,
  venuePageId: string,
  options?: { forPublicDisplay?: boolean }
): Promise<VenueCarouselResponse> {
  // 1. Fetch venue page to get carousel_mode and auto-rotate settings
  const { data: venuePage, error: venueError } = await supabase
    .from("venue_pages")
    .select("carousel_mode, carousel_auto_rotate_delay, timezones")
    .eq("id", venuePageId)
    .single();

  if (venueError) {
    logException(venueError);
    throw venueError;
  }

  const mode = (venuePage?.carousel_mode || "default") as VenuePageCarouselMode;
  const autoRotateDelay = venuePage?.carousel_auto_rotate_delay ?? null;
  const venueTimezones = (venuePage?.timezones ?? []) as VenuePageTimezones;

  // If mode is default, return empty items (frontend will use default logic)
  if (mode === "default") {
    return { mode, autoRotateDelay, items: [] };
  }

  // 2. Fetch carousel items with related data
  const { data: carouselItems, error: itemsError } = await supabase
    .from("venue_pages_carousel_items")
    .select(
      `
      id,
      type,
      order,
      show_id,
      image_id,
      video_id,
      venue_pages_images!image_id(id, image_id, alt_text),
      venue_pages_videos!video_id(id, video_id, title)
    `
    )
    .eq("venue_page_id", venuePageId)
    .order("order", { ascending: true });

  if (itemsError) {
    logException(itemsError);
    throw itemsError;
  }

  // 3. Batch fetch all shows in one query (instead of N+1 individual queries)
  const showIds = (carouselItems || [])
    .filter((item) => item.type === "show" && item.show_id)
    .map((item) => item.show_id as string);

  let showsMap: Map<string, ShowWithComedian> =
    showIds.length > 0 ? await getShowsByIds(showIds) : new Map();

  if (options?.forPublicDisplay && showsMap.size > 0) {
    showsMap = await filterAndEnrichShowsForPublicDisplay(
      showsMap,
      venueTimezones
    );
  }

  // 4. Transform items to response format
  const items: VenueCarouselItem[] = [];

  for (const item of carouselItems || []) {
    if (item.type === "show" && item.show_id) {
      const show = showsMap.get(item.show_id);
      if (show) {
        items.push({
          type: "show",
          id: item.id,
          order: item.order,
          show,
        });
      }
    } else if (item.type === "image" && item.venue_pages_images) {
      const imageData = Array.isArray(item.venue_pages_images)
        ? item.venue_pages_images[0]
        : item.venue_pages_images;
      if (imageData) {
        items.push({
          type: "image",
          id: item.id,
          order: item.order,
          image: {
            id: imageData.id,
            imageId: imageData.image_id,
            imageUrl: getCDNImageURL(imageData.image_id),
            altText: imageData.alt_text,
          },
        });
      }
    } else if (item.type === "video" && item.venue_pages_videos) {
      const videoData = Array.isArray(item.venue_pages_videos)
        ? item.venue_pages_videos[0]
        : item.venue_pages_videos;
      if (videoData) {
        items.push({
          type: "video",
          id: item.id,
          order: item.order,
          video: {
            id: videoData.id,
            videoId: videoData.video_id,
            title: videoData.title,
          },
        });
      }
    }
  }

  return { mode, autoRotateDelay, items };
}

/**
 * Update carousel mode and optionally items (full replace for add/remove)
 * If items is not provided, only the mode is updated and existing items are preserved.
 */
export async function updateVenuePageCarousel(
  supabase: SupabaseClient<Database>,
  venuePageId: string,
  data: { mode: VenuePageCarouselMode; items?: CarouselItemInput[] }
): Promise<VenueCarouselResponse> {
  const { mode, items } = data;

  // 1. Update carousel_mode on venue_pages
  const { error: modeError } = await supabase
    .from("venue_pages")
    .update({ carousel_mode: mode })
    .eq("id", venuePageId);

  if (modeError) {
    logException(modeError);
    throw modeError;
  }

  // 2. Only update items if explicitly provided
  if (items !== undefined) {
    // Delete all existing carousel items
    const { error: deleteError } = await supabase
      .from("venue_pages_carousel_items")
      .delete()
      .eq("venue_page_id", venuePageId);

    if (deleteError) {
      logException(deleteError);
      throw deleteError;
    }

    // Insert new items (if mode is custom and items provided)
    if (mode === "custom" && items.length > 0) {
      const insertData = items.map((item, index) => ({
        venue_page_id: venuePageId,
        type: item.type,
        show_id: item.type === "show" ? item.showId : null,
        image_id: item.type === "image" ? item.imageId : null,
        video_id: item.type === "video" ? item.videoId : null,
        order: item.order ?? index,
      }));

      const { error: insertError } = await supabase
        .from("venue_pages_carousel_items")
        .insert(insertData);

      if (insertError) {
        logException(insertError);
        throw insertError;
      }
    }
  }

  // 3. Return updated carousel data
  return getVenuePageCarousel(supabase, venuePageId);
}

/**
 * Update carousel order only (lightweight, for drag-and-drop)
 */
export async function updateCarouselOrder(
  supabase: SupabaseClient<Database>,
  venuePageId: string,
  itemIds: string[]
): Promise<void> {
  // Verify all items belong to this venue page
  const { data: existingItems, error: fetchError } = await supabase
    .from("venue_pages_carousel_items")
    .select("id")
    .eq("venue_page_id", venuePageId)
    .in("id", itemIds);

  if (fetchError) {
    logException(fetchError);
    throw fetchError;
  }

  if (!existingItems || existingItems.length !== itemIds.length) {
    throw new Error("Some items don't belong to this venue page");
  }

  // Update order for each item (scoped to venue_page_id for security)
  const updatePromises = itemIds.map((id, index) =>
    supabase
      .from("venue_pages_carousel_items")
      .update({ order: index })
      .eq("id", id)
      .eq("venue_page_id", venuePageId)
  );

  const results = await Promise.all(updatePromises);

  // Check for any errors
  const errorResult = results.find((r) => r.error);
  if (errorResult?.error) {
    logException(errorResult.error);
    throw errorResult.error;
  }
}

/**
 * Create a new carousel image and add it to the carousel
 */
export async function createVenuePageCarouselImage(
  supabase: SupabaseClient<Database>,
  venuePageId: string,
  data: { imageId: string; altText?: string }
): Promise<UploadCarouselImageResponse> {
  // 1. Insert the image record
  const { data: image, error } = await supabase
    .from("venue_pages_images")
    .insert({
      venue_page_id: venuePageId,
      image_id: data.imageId,
      alt_text: data.altText || null,
    })
    .select()
    .single();

  if (error) {
    logException(error);
    throw error;
  }

  // 2. Get the current max order for this venue's carousel items
  const { data: maxOrderResult } = await supabase
    .from("venue_pages_carousel_items")
    .select("order")
    .eq("venue_page_id", venuePageId)
    .order("order", { ascending: false })
    .limit(1)
    .maybeSingle();

  const nextOrder = (maxOrderResult?.order ?? -1) + 1;

  // 3. Create a carousel item referencing this image
  const { error: carouselItemError } = await supabase
    .from("venue_pages_carousel_items")
    .insert({
      venue_page_id: venuePageId,
      type: "image",
      image_id: image.id,
      order: nextOrder,
    });

  if (carouselItemError) {
    logException(carouselItemError);
    throw carouselItemError;
  }

  return {
    id: image.id,
    imageUrl: getCDNImageURL(image.image_id),
  };
}

/**
 * Create a new carousel video and add it to the carousel
 */
export async function createVenuePageCarouselVideo(
  supabase: SupabaseClient<Database>,
  venuePageId: string,
  data: { videoId: string; title?: string }
): Promise<UploadCarouselVideoResponse> {
  // 1. Insert the video record
  const { data: video, error } = await supabase
    .from("venue_pages_videos")
    .insert({
      venue_page_id: venuePageId,
      video_id: data.videoId,
      title: data.title || null,
    })
    .select()
    .single();

  if (error) {
    logException(error);
    throw error;
  }

  // 2. Get the current max order for this venue's carousel items
  const { data: maxOrderResult } = await supabase
    .from("venue_pages_carousel_items")
    .select("order")
    .eq("venue_page_id", venuePageId)
    .order("order", { ascending: false })
    .limit(1)
    .maybeSingle();

  const nextOrder = (maxOrderResult?.order ?? -1) + 1;

  // 3. Create a carousel item referencing this video
  const { error: carouselItemError } = await supabase
    .from("venue_pages_carousel_items")
    .insert({
      venue_page_id: venuePageId,
      type: "video",
      video_id: video.id,
      order: nextOrder,
    });

  if (carouselItemError) {
    logException(carouselItemError);
    throw carouselItemError;
  }

  return {
    id: video.id,
    videoId: video.video_id,
  };
}

/**
 * Delete a carousel image and any carousel items referencing it
 * Returns true if the image was found and deleted, false if not found
 */
export async function deleteVenuePageCarouselImage(
  supabase: SupabaseClient<Database>,
  imageId: string
): Promise<boolean> {
  // CASCADE will handle deleting carousel items referencing this image
  const { data, error } = await supabase
    .from("venue_pages_images")
    .delete()
    .eq("id", imageId)
    .select("id");

  if (error) {
    logException(error);
    throw error;
  }

  return data !== null && data.length > 0;
}

/**
 * Delete a carousel video and any carousel items referencing it
 */
export async function deleteVenuePageCarouselVideo(
  supabase: SupabaseClient<Database>,
  videoId: string
): Promise<void> {
  // CASCADE will handle deleting carousel items referencing this video
  const { error } = await supabase
    .from("venue_pages_videos")
    .delete()
    .eq("id", videoId);

  if (error) {
    logException(error);
    throw error;
  }
}

/**
 * Bulk create carousel items for shows
 */
export async function createVenuePageCarouselShows(
  supabase: SupabaseClient<Database>,
  venuePageId: string,
  showIds: string[]
): Promise<AddCarouselShowsResponse> {
  if (showIds.length === 0) {
    return { success: true, addedCount: 0 };
  }

  // Get the current max order for this venue's carousel items
  const { data: maxOrderResult } = await supabase
    .from("venue_pages_carousel_items")
    .select("order")
    .eq("venue_page_id", venuePageId)
    .order("order", { ascending: false })
    .limit(1)
    .single();

  const startOrder = (maxOrderResult?.order ?? -1) + 1;

  // Create carousel items for each show
  const insertData = showIds.map((showId, index) => ({
    venue_page_id: venuePageId,
    type: "show" as const,
    show_id: showId,
    order: startOrder + index,
  }));

  const { error } = await supabase
    .from("venue_pages_carousel_items")
    .insert(insertData);

  if (error) {
    logException(error);
    throw error;
  }

  return { success: true, addedCount: showIds.length };
}

/**
 * Delete a show carousel item
 */
export async function deleteVenuePageCarouselShow(
  supabase: SupabaseClient<Database>,
  venuePageId: string,
  showId: string
): Promise<boolean> {
  const { data, error } = await supabase
    .from("venue_pages_carousel_items")
    .delete()
    .eq("venue_page_id", venuePageId)
    .eq("show_id", showId)
    .eq("type", "show")
    .select("id");

  if (error) {
    logException(error);
    throw error;
  }

  return data !== null && data.length > 0;
}
